import { useTranslation } from "react-i18next";
import { Button, Checkbox, Label } from "@tellescope/ui";
import { EnduserSearchCombobox } from "../EnduserSearchCombobox";
import { useVideoCallDialer } from "./useVideoCallDialer";

export interface VideoCallDialerPanelProps {
  /** Callback when the panel is closed */
  onClose: () => void;
}

/**
 * VideoCallDialerPanel - UI for starting an ad-hoc video call.
 *
 * Lets the user select one or more endusers, optionally notify them via SMS, then creates a
 * transient video-enabled calendar event and opens the call. An email invite is sent
 * automatically to any selected enduser that has an email on file.
 */
export const VideoCallDialerPanel = ({ onClose }: VideoCallDialerPanelProps) => {
  const { t } = useTranslation("calling");
  const { selectedEndusers, setSelectedEndusers, sendSMS, setSendSMS, onStartVideoCall, isSubmitting, canStart } =
    useVideoCallDialer();

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-2">
        <Label>{t("Contacts")}</Label>
        <EnduserSearchCombobox
          value={selectedEndusers}
          onChange={setSelectedEndusers}
          multiple
          showAsBadges
          defaultRendererVariant="phone"
          customFilter={(searchTerm, options) => {
            const lower = searchTerm.toLowerCase();
            return options.filter((opt) => {
              const enduser = opt.metadata?.enduser;
              return (
                opt.label.toLowerCase().includes(lower) ||
                opt.value.toLowerCase().includes(lower) ||
                enduser?.phone?.includes(searchTerm) ||
                enduser?.email?.toLowerCase().includes(lower)
              );
            });
          }}
          placeholder={t("Search contacts...")}
          searchPlaceholder={t("Type to search...")}
          emptyText={t("No contacts found")}
          clearable
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="video-call-send-sms"
            checked={sendSMS}
            onCheckedChange={(checked) => setSendSMS(checked === true)}
          />
          <Label htmlFor="video-call-send-sms" className="font-normal">
            {t("Send invite via SMS")}
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("An email invite is sent automatically to contacts with an email on file.")}
        </p>
      </div>
      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          {t("Cancel")}
        </Button>
        <Button
          variant="default"
          onClick={onStartVideoCall}
          disabled={!canStart}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          {isSubmitting ? t("Starting...") : t("Start video call")}
        </Button>
      </div>
    </div>
  );
};
